/**
 * Static training content bundle for the Training page.
 *
 * MASTER COPY: ui/nurture-app-android/app/src/main/java/com/bhuvanafortunegreens/
 * operatorapp/training/TrainingContent.kt — this file is a mechanical port of it.
 * Keep the two in lockstep: same ids, order, titles, summaries, body text
 * (newline-preserved), facts, and the exact GCS URLs. Edit the Kotlin file
 * first, then mirror the change here.
 */

export type TrainingMediaType = 'image' | 'video';

export interface TrainingMedia {
  type: TrainingMediaType;
  url: string;
  caption?: string;
}

export interface KnowledgeModule {
  id: string;
  order: number;
  title: string;
  /** One-line description shown on the collapsed row. */
  summary: string;
  /** Full text, newline-formatted, rendered pre-wrap. */
  body: string;
  media: TrainingMedia[];
}

export interface WebsiteFact {
  label: string;
  value: string;
}

export interface RecordingEntry {
  url: string;
  uploadedAt?: string;
  filename?: string;
  mimeType?: string;
  size?: number;
}

export interface SampleCall {
  id: string;
  title: string;
  recording: RecordingEntry;
}

export interface TrainingVideo {
  id: string;
  title: string;
  description: string;
  url: string;
}

export const websiteUrl = "https://bhuvanafortunegreens.com";

export const websiteFacts: WebsiteFact[] = [
  {
    label: "Project",
    value: "Bhuvana's Fortune Greens — weekend villa community",
  },
  {
    label: "Location",
    value: "Pothanshetipally, Masaipet Mandal, Medak District, Telangana",
  },
  {
    label: "Scale",
    value: "85+ acres, 316 exclusive villas",
  },
  {
    label: "Plot sizes",
    value: "600–900 sq yards",
  },
  {
    label: "Villa styles",
    value: "Kerala House, Bali House, Duplex, Manduva",
  },
  {
    label: "Clubhouse",
    value: "30,000 sq ft with 100+ amenities",
  },
  {
    label: "Price from",
    value: "Rs 1.70 Crore",
  },
  {
    label: "Approvals",
    value: "RERA & DTCP approved",
  },
  {
    label: "Possession",
    value: "September 2026",
  },
  {
    label: "Sales phone",
    value: "+91 95500 07046",
  },
  {
    label: "Sales email",
    value: "sales@bhuvanafortunegreens.in",
  },
];

export const projectKnowledge: KnowledgeModule[] = [
  {
    id: "welcome",
    order: 1,
    title: "Welcome to Fortune Greens",
    summary: "First message every new lead receives — intro video + key highlights.",
    body: "Premium Weekend Villas on 85+ Acres\n\nKey facts:\n• 85+ acres in Masaipet, 5 mins from the Regional Ring Road\n• 316 exclusive villas on 600–900 sq yard plots\n• 4 villa styles: Kerala House, Bali House, Duplex, Manduva\n• 30,000 sq ft clubhouse with 100+ amenities\n• Starting from Rs 1.70 Crore\n• RERA & DTCP approved\n• Possession: September 2026\n\nTone: warm welcome, excitement about the project, invite to explore.",
    media: [
      {
        type: "video",
        url: "https://storage.googleapis.com/bhuvana-public/fortunegreens/kb/videos/welcome_intro.mp4",
        caption: "30-second intro — aerial views, villa styles, amenities",
      },
    ],
  },
  {
    id: "location",
    order: 2,
    title: "Location & Connectivity",
    summary: "Where the project is, drive times, and why the corridor is strategic.",
    body: "Address: Pothanshetipally, Masaipet Mandal, Medak District, Telangana 502335\n\nDrive times:\n• NH-44 (Hyderabad–Nizamabad Highway) — 2 km, 2 mins\n• Proposed Regional Ring Road (RRR) — 3 km, 5 mins\n• Toopran Town — 5 km, 8 mins\n• Kandlakoya & ORR — 35 km, 30 mins\n• Kompally — 40 km, 35 mins\n• Shamirpet Lake — 45 km, 45 mins\n• Secunderabad Railway Station — 55 km, 55 mins\n• Financial District, Gachibowli — 60 km, 75 mins\n\nConnectivity highlights:\n• Smooth National Highway drive with no city traffic\n• Close enough for a Friday evening drive, far enough to feel like a getaway\n• Upcoming RRR will make this even more accessible\n• RRR construction is already driving land-value appreciation in this corridor\n• Located in the North Hyderabad growth corridor",
    media: [
      {
        type: "image",
        url: "https://storage.googleapis.com/bhuvana-public/fortunegreens/kb/images/aerial_layout.png",
        caption: "Aerial top-view of the 85-acre layout",
      },
    ],
  },
  {
    id: "builder_credibility",
    order: 3,
    title: "About Bhuvana Group",
    summary: "Builder background, completed projects, RERA/DTCP approvals, awards.",
    body: "Bhuvana Group — founded 2019. Mission: blend modern living with nature’s serenity.\nFounders: Jaipal Reddy, Dayakar Reddy, Vinay Veerabattini, Vikram Bairagoni.\n\nCompleted projects (both delivered on time):\n• Urban Greens — 2 & 3 BHK luxury apartments, Kompally\n• Green Valley — open plots, Kompally\n\nLegal & compliance:\n• RERA: P01000008766, P01000006334 (verify at rera.telangana.gov.in)\n• DTCP approvals: TLP.no.95/2023/H, TLP.no.120/2024/H\n• Clear and marketable title — all legal documents available for verification\n\nTrust points:\n• RERA numbers are independently verifiable online\n• Builder has successfully delivered two projects on time\n• Growing community of IT professionals, business owners, senior executives\n• 24/7 security with CCTV and solar-fence perimeter",
    media: [
      {
        type: "video",
        url: "https://storage.googleapis.com/bhuvana-public/fortunegreens/kb/videos/bhuvana_awards_compressed.mp4",
        caption: "Bhuvana Group awards and recognition",
      },
    ],
  },
  {
    id: "amenities",
    order: 4,
    title: "100+ Amenities at Fortune Greens",
    summary: "Full amenities breakdown across 5 categories.",
    body: "30,000 sq ft clubhouse with 100+ amenities across 5 categories.\n\nClubhouse & Wellness:\n• Swimming pool (main + toddlers pool + poolside deck)\n• Fitness centre (state-of-the-art gym)\n• Spa & wellness centre (steam, sauna)\n• Deluxe suite rooms (guest accommodation)\n• Snacking zone / cafe (poolside BBQ)\n• Gathering plaza (multipurpose hall), welcome lounge\n• Walk-way deck & pergola, Wi-Fi throughout\n\nSports & Recreation:\n• Pickleball, tennis, badminton, basketball, volleyball courts\n• Cricket nets & box cricket, mini golf\n• Cycling track, jogging track\n• Toddlers play area & swings, dog run area\n\nParks & Gardens:\n• Central Park (amphitheatre, stage, water fountain, party lawn)\n• Butterfly Park (water pond, bird park, camping, outdoor yoga)\n• Brook Park, Triangle Park, large landscaped parks\n• Meditation & healing gardens, flower garden\n\nUnique Features:\n• Tree house, camping area\n• Goshala (traditional cow shelter), polyhouse (organic farming), fruit garden\n• Destination wedding venue, outdoor auditorium, avenue plantation\n\nSecurity & Infrastructure:\n• Grand entrance with security booth, solar-fence perimeter\n• 24/7 CCTV surveillance, backup power station, safe pedestrian pathways",
    media: [
      {
        type: "image",
        url: "https://storage.googleapis.com/bhuvana-public/fortunegreens/kb/images/clubhouse_pool.png",
        caption: "Clubhouse & Wellness — pool, spa, gym, guest suites",
      },
      {
        type: "image",
        url: "https://storage.googleapis.com/bhuvana-public/fortunegreens/kb/images/sports_cricket.png",
        caption: "Sports & Recreation — courts, cricket, golf, tracks",
      },
      {
        type: "image",
        url: "https://storage.googleapis.com/bhuvana-public/fortunegreens/kb/images/central_park.png",
        caption: "Parks & Gardens — Central Park, Butterfly Park, meditation gardens",
      },
      {
        type: "image",
        url: "https://storage.googleapis.com/bhuvana-public/fortunegreens/kb/images/camping_area.png",
        caption: "Unique Features — tree house, camping, organic farm, wedding venue",
      },
      {
        type: "image",
        url: "https://storage.googleapis.com/bhuvana-public/fortunegreens/kb/images/park_entrance.png",
        caption: "Security & Infrastructure — grand entrance, solar fence, CCTV",
      },
    ],
  },
  {
    id: "project_highlights",
    order: 5,
    title: "Project Highlights",
    summary: "Key differentiators — scale, low density, villa variety, approvals.",
    body: "Scale:\n• 85+ acres total area\n• 316 villas — very low density\n• 600–900 sq yard plots (5,400–8,100 sq ft per plot)\n• 10+ acres dedicated to parks and green spaces\n• 30,000 sq ft clubhouse with 100+ amenities\n\nVilla styles:\n• Kerala House — traditional sloped-roof charm\n• Bali House — tropical resort-style elegance\n• Duplex Villa — contemporary G+1 design\n• Manduva Villa — classic Indian heritage\n\nKey facts:\n• RERA & DTCP approved\n• Possession: September 2026\n• Bank loans available (SBI, HDFC, ICICI, Axis)\n• Construction-linked payment plan\n• One of the largest farmhouse communities near Hyderabad\n\nDifferentiators:\n• 4 unique villa styles to match different personalities\n• 600–900 sq yard plots — far more spacious than typical villa projects\n• 85+ acres means ample breathing room, not a cramped layout\n• 100+ amenities including unique ones — tree house, goshala, polyhouse",
    media: [
      {
        type: "image",
        url: "https://storage.googleapis.com/bhuvana-public/fortunegreens/kb/images/aerial_view.png",
        caption: "Aerial angle view of the layout",
      },
    ],
  },
  {
    id: "lifestyle",
    order: 6,
    title: "Lifestyle @ Fortune Greens",
    summary: "What a weekend looks like — the private-resort experience.",
    body: "A typical weekend:\n• Drive down Friday evening, wake up to birdsong Saturday morning\n• Morning swim at the clubhouse pool, kids at the toddlers pool\n• A game of pickleball or cricket with neighbours\n• Evening walk through the butterfly park and meditation gardens\n• Poolside BBQ with friends and family at the cafe\n• Kids at the tree house and camping area\n• Sunday morning yoga at the outdoor yoga area\n\nYour own private resort:\n• No more weekend resort bookings\n• Invite 200+ guests for events — party lawns, amphitheatre, wedding venue\n• Guest suite rooms in the clubhouse for visitors\n• Organic farming at the polyhouse, fruit from your garden\n• Dog run area for pet lovers\n\nThe resort-cost comparison:\n• Typical weekend spend: Rs 10,000–15,000 on resort stays\n• Annual spend: Rs 5–8 lakhs/year\n• Over 10 years: Rs 50–80 lakhs with nothing to show for it\n• Here, you own a villa that appreciates in value\n\nIdeal for: weekend getaways for IT professionals & business owners,\nfamily weekends, hosting events, a retirement home, a personal retreat.\n\nTone: paint a vivid picture — make them feel what it’s like to be there.",
    media: [
      {
        type: "image",
        url: "https://storage.googleapis.com/bhuvana-public/fortunegreens/kb/images/water_pond.png",
        caption: "Serene water pond area",
      },
    ],
  },
  {
    id: "construction_update",
    order: 7,
    title: "Latest Construction Update",
    summary: "Construction progress — on track for September 2026 possession.",
    body: "Status: under construction — on track for September 2026 possession.\n\nProgress highlights:\n• Roads and internal infrastructure taking shape\n• Villa construction progressing across the layout\n• 85-acre layout visible from aerial views\n• Regular progress updates shared with interested buyers\n\nTransparency:\n• We share construction updates regularly — no surprises\n• Site visits run every week for a firsthand progress check\n• RERA registration ensures regulatory oversight\n• Construction-linked payment plan — you pay as milestones are hit",
    media: [
      {
        type: "video",
        url: "https://storage.googleapis.com/bhuvana-public/fortunegreens/marketing-assets/20260808_construction-update-jul-2026.mp4",
        caption: "July 2026 progress — villa-by-villa status, clubhouse foundation, amenities",
      },
    ],
  },
  {
    id: "pricing",
    order: 8,
    title: "Pricing Details",
    summary: "Villa-wise pricing, what is included, payment structure.",
    body: "Pricing:\n• 2 BHK — 1200–1800 sq ft built-up, 600 sq yd plot — Rs 1.70–1.90 Cr\n• 3 BHK — 1800–2700 sq ft built-up, 600–900 sq yd plot — Rs 1.90–2.84 Cr\n• Duplex G+1 — 3200 sq ft built-up, 600–900 sq yd plot — Rs 2.34–3.00 Cr\n\nWhat’s included:\n• Plot + villa construction\n• Vitrified tiles (RAK/Kajaria, 800x800mm)\n• Jaquar / American Standard fittings\n• uPVC windows (Fenesta or equivalent)\n• Concealed copper wiring (Finolex/Anchor)\n• 30 plug points, 6 AC points, 3-phase supply\n• B.T. wood frame doors with melamine polishing\n\nPayment structure:\n• Construction-linked payment plan — pay in milestones\n• Up to 80% bank loan available (SBI, HDFC, ICICI, Axis Bank)\n\nSpecifications:\n• Structure: R.C.C.-framed (Modern 4BHK) / Steel frame (Kerala & Bali House)\n• Walls: external 9 inch, internal 4.5 inch red brick masonry\n• Flooring: vitrified porcelain tiles (RAK/Kajaria)\n• Bathrooms: designer tiles, Jaquar/American Standard, concealed tanks\n• Painting: texture-based external, Acrylic Royal emulsion internal\n• Electrical: concealed copper wiring, MCB breakers, 3-phase supply\n\nDisclaimer: prices are indicative and subject to availability. GST and\nregistration charges are additional as per government norms.",
    media: [],
  },
  {
    id: "financing",
    order: 9,
    title: "Bank Loans & Financing",
    summary: "Loan options, pre-approved banks, EMI example.",
    body: "Loan options:\n• Up to 80% of villa cost financed\n• Pre-approved banks: SBI, HDFC, ICICI, Axis Bank\n• Competitive rates (varies by bank and borrower profile)\n\nPayment structure:\n• Construction-linked payment plan — pay in milestones as construction progresses\n• No need to pay the full amount upfront\n• EMI starts only on the disbursed amount during construction\n\nIndicative EMI example (3 BHK):\n• Villa cost: ~Rs 2.55 Cr\n• Down payment: ~Rs 51 lakhs (20%)\n• Loan amount: ~Rs 2.04 Cr\n• EMI: ~Rs 1.6 lakh/month (at ~9% for 20 years)\n• Approximate figures — actual EMI depends on the bank, your profile,\n  and current interest rates.\n\nNext step: our team can connect the lead with the bank relationship\nmanager for exact numbers and pre-approval.",
    media: [],
  },
  {
    id: "roi",
    order: 10,
    title: "Return on Investment",
    summary: "Why the location appreciates, lifestyle math, rental potential.",
    body: "Location appreciation:\n• 5 mins from the upcoming Regional Ring Road (RRR) — major infrastructure driver\n• North Hyderabad growth corridor with rapid development\n• 2 km from NH-44 — one of India’s busiest national highways\n• Land values in this belt have been appreciating steadily\n• RRR construction will drive significant further value appreciation\n\nLifestyle math:\n• Typical resort spend: Rs 10,000–15,000 per weekend\n• Annual: Rs 5–8 lakhs/year — over 10 years, Rs 50–80 lakhs spent\n• At Fortune Greens you own a villa that appreciates while enjoying the\n  same getaway experience\n\nRental potential:\n• Weekend-getaway demand near Hyderabad is high and growing\n• Airbnb and short-term rental potential\n• 100+ amenities and clubhouse make it a highly desirable rental\n• Destination wedding venue adds event-rental opportunity\n\nWhat you get: a real asset on 600–900 sq yard plots, RERA-registered\nwith clear title, in a growth corridor, plus a lifestyle upgrade.\n\nTone: factual and honest — share facts, let them decide.\nNever create fake urgency.",
    media: [],
  },
  {
    id: "testimonials",
    order: 11,
    title: "Customer Testimonials",
    summary: "Testimonial video + written quotes from buyers and visitors.",
    body: "Rajesh Kumar — IT Professional, Gachibowli (Urban Greens customer):\n\"We were looking for a weekend getaway close to Hyderabad and Fortune\nGreens was exactly what we needed. The Kerala-style villas are stunning\nand the amenities are world-class.\"\n\nPriya & Venkat Reddy — Business Owners, Kompally (Urban Greens customers):\n\"Bhuvana Group delivered exactly what they promised with Urban Greens.\nNow we’re investing in Fortune Greens for our retirement. The location\nnear RRR makes it perfect.\"\n\nSrinivas Rao — Senior Executive, HITEC City (site-visit feedback):\n\"The 85-acre development with only 316 villas means lots of open space.\nOur kids love the nature trails and we love the peaceful weekends away\nfrom the city.\"\n\nWe can connect a lead with existing buyers if they’d like a firsthand\nperspective. These are from customers of Bhuvana Group’s previous\nprojects and Fortune Greens site visitors.",
    media: [
      {
        type: "video",
        url: "https://storage.googleapis.com/bhuvana-public/fortunegreens/kb/videos/customer_testimonial_1_compressed.mp4",
        caption: "Customer testimonial from Bhuvana Group buyers",
      },
    ],
  },
  {
    id: "villa_videos",
    order: 12,
    title: "Villa Walkthrough Videos",
    summary: "Walkthroughs of all 4 villa styles with specs and pricing.",
    body: "Kerala House — traditional charm with sloped roofs & verandahs:\n• 2 BHK — 1200 sft, 600 sq yds — Rs 1.70 Cr\n• 3 BHK — 1800 sft, 900 sq yds — Rs 2.55 Cr\n• Courtyard spaces, classic Kerala architecture, sloped-roof design\n\nBali House — tropical elegance with open layouts & high ceilings:\n• 2 BHK — 1800 sft, 600 sq yds — Rs 1.90 Cr\n• 3 BHK — 2700 sft, 900 sq yds — Rs 2.84 Cr\n• Open layouts, high ceilings, resort-style living\n\nDuplex Villa — contemporary G+1 design for modern families:\n• G+1 — 3200 sft (1600+1600), 600 sq yds — Rs 2.34 Cr\n• G+1 — 3200 sft (1600+1600), 900 sq yds — Rs 3.00 Cr\n• Modern architecture, practical space planning, two-floor living\n\nManduva Villa — classic Indian heritage architecture:\n• 3 BHK — 1800 sft, 600 sq yds — Rs 1.90 Cr\n• 3 BHK — 1800 sft, 900 sq yds — Rs 2.55 Cr\n• Heritage design, classic Indian architecture, one of a kind\n\nCommon specs: vitrified tiles (RAK/Kajaria), Jaquar / American Standard\nfittings, uPVC windows (Fenesta), concealed copper wiring (Finolex),\n30 plug points, 6 AC points, 3-phase supply.",
    media: [
      {
        type: "image",
        url: "https://storage.googleapis.com/bhuvana-public/fortunegreens/kb/images/kerala_house_render.png",
        caption: "Kerala House — render",
      },
      {
        type: "video",
        url: "https://storage.googleapis.com/bhuvana-public/fortunegreens/marketing-assets/20260319_191633_kerala_house_walkthrough_compressed.mp4",
        caption: "Kerala House walkthrough",
      },
      {
        type: "image",
        url: "https://storage.googleapis.com/bhuvana-public/fortunegreens/kb/images/bali_house_render.png",
        caption: "Bali House — render",
      },
      {
        type: "video",
        url: "https://storage.googleapis.com/bhuvana-public/fortunegreens/kb/videos/bali_house_walkthrough.mp4",
        caption: "Bali House walkthrough",
      },
      {
        type: "image",
        url: "https://storage.googleapis.com/bhuvana-public/fortunegreens/kb/images/duplex_villa_render.png",
        caption: "Duplex Villa — render",
      },
      {
        type: "video",
        url: "https://storage.googleapis.com/bhuvana-public/fortunegreens/kb/videos/duplex_villa_walkthrough.mp4",
        caption: "Duplex Villa walkthrough",
      },
      {
        type: "image",
        url: "https://storage.googleapis.com/bhuvana-public/fortunegreens/kb/images/weekend_villas_hero.png",
        caption: "Manduva Villa — render",
      },
      {
        type: "video",
        url: "https://storage.googleapis.com/bhuvana-public/fortunegreens/kb/videos/manduva_villa_walkthrough.mp4",
        caption: "Manduva Villa walkthrough",
      },
    ],
  },
];

export const shortVideos: TrainingVideo[] = [
  {
    id: "project_intro",
    title: "Project Intro",
    description: "Murali Mohan garu at the site, drone views, the NH-44 route and the ₹1.7 Cr price — sent early in a conversation.",
    url: "https://bhuvana-album-media.s3.ap-south-2.amazonaws.com/album/a06.mp4",
  },
  {
    id: "construction_update",
    title: "Construction Update — July",
    description: "Latest construction progress across the layout.",
    url: "https://storage.googleapis.com/bhuvana-public/fortunegreens/marketing-assets/20260808_construction-update-jul-2026.mp4",
  },
  {
    id: "testimonial",
    title: "Customer Testimonial — CVN Shastry",
    description: "A customer shares their experience with Bhuvana Group.",
    url: "https://storage.googleapis.com/bhuvana-public/fortunegreens/marketing-assets/20260319_191645_cvn-shastry-testimonial.mp4",
  },
  {
    id: "kerala_house",
    title: "Kerala House Walkthrough",
    description: "Traditional charm with sloped roofs and verandahs.",
    url: "https://storage.googleapis.com/bhuvana-public/fortunegreens/marketing-assets/20260319_191633_kerala_house_walkthrough_compressed.mp4",
  },
  {
    id: "bali_house",
    title: "Bali House Walkthrough",
    description: "Tropical elegance with open layouts and high ceilings.",
    url: "https://storage.googleapis.com/bhuvana-public/fortunegreens/marketing-assets/20260319_191627_bali_house_walkthrough.mp4",
  },
  {
    id: "duplex_villa",
    title: "Duplex Villa Walkthrough",
    description: "Contemporary G+1 design for modern families.",
    url: "https://storage.googleapis.com/bhuvana-public/fortunegreens/marketing-assets/20260319_191630_duplex_villa_walkthrough.mp4",
  },
  {
    id: "manduva_villa",
    title: "Manduva Villa Walkthrough",
    description: "Classic Indian heritage architecture.",
    url: "https://storage.googleapis.com/bhuvana-public/fortunegreens/marketing-assets/20260319_191637_manduva_villa_walkthrough.mp4",
  },
];

export const sampleCalls: SampleCall[] = [
  {
    id: "3de23f41-2ee5-4832-95ac-52fa3603fe47",
    title: "Booking a same-day site visit",
    recording: {
      url: "https://storage.googleapis.com/magicmetrics-call-recordings/calls/3de23f41-2ee5-4832-95ac-52fa3603fe47/b5f831d8216348ed8c2e57286f8fc9a1.m4a",
      uploadedAt: "2026-06-13T20:39:31.615284+00:00",
      filename: "Call recording 260613_prashanth.m4a",
      mimeType: "audio/m4a",
      size: 1524247,
    },
  },
  {
    id: "5300fe3a-df5b-4374-867c-c11fd5cfb351",
    title: "Building rapport with a serious investor",
    recording: {
      url: "https://storage.googleapis.com/magicmetrics-call-recordings/calls/5300fe3a-df5b-4374-867c-c11fd5cfb351/d23ac90544414d5abf27fbb48ba54e80.m4a",
      uploadedAt: "2026-06-18T04:26:12.339284+00:00",
      filename: "Call recording K Himagiri Naidu_260618_094535.m4a",
      mimeType: "audio/m4a",
      size: 10195879,
    },
  },
  {
    id: "05ca3067-9f9d-4807-825d-9558e50645af",
    title: "Handling an inquiry for family abroad",
    recording: {
      url: "https://storage.googleapis.com/magicmetrics-call-recordings/calls/05ca3067-9f9d-4807-825d-9558e50645af/71f3659b551f4d1493edb58b290d335e.m4a",
      uploadedAt: "2026-06-13T20:56:05.984322+00:00",
      filename: "Call recording ashish_260518_112004.m4a",
      mimeType: "audio/m4a",
      size: 12983969,
    },
  },
  {
    id: "ccab083f-099b-4fc8-b68d-6628f0c1f6b2",
    title: "A warm, personal conversation",
    recording: {
      url: "https://storage.googleapis.com/magicmetrics-call-recordings/calls/ccab083f-099b-4fc8-b68d-6628f0c1f6b2/1712c0840bf3423081ccb116166496b1.m4a",
      uploadedAt: "2026-06-13T20:56:47.117191+00:00",
      filename: "Call recording Prasad Jayanthy_260513_104212.m4a",
      mimeType: "audio/m4a",
      size: 10396884,
    },
  },
  {
    id: "7b5bb2e5-ae8b-4956-b3f3-2ea25c67a0d8",
    title: "Recognising a wrong-fit caller",
    recording: {
      url: "https://storage.googleapis.com/magicmetrics-call-recordings/calls/7b5bb2e5-ae8b-4956-b3f3-2ea25c67a0d8/b6ff23f6e4ba4196b2a88a3360e5eeba.m4a",
      uploadedAt: "2026-06-17T07:41:44.926881+00:00",
      filename: "Call recording Nisar_260617_131050.m4a",
      mimeType: "audio/m4a",
      size: 735682,
    },
  },
  {
    id: "80c57c5a-2af5-422a-83ca-ada19dd2f379",
    title: "Answering a home-loan question",
    recording: {
      url: "https://storage.googleapis.com/magicmetrics-call-recordings/calls/80c57c5a-2af5-422a-83ca-ada19dd2f379/e92c3e40c3994fa2a26e653e1cc0a739.m4a",
      uploadedAt: "2026-06-17T07:48:04.640979+00:00",
      filename: "Call recording Shylaja_260617_095801.m4a",
      mimeType: "audio/m4a",
      size: 7325980,
    },
  },
  {
    id: "bc1b88a3-0784-4458-a252-54708005256d",
    title: "Explaining pricing and offering pickup",
    recording: {
      url: "https://storage.googleapis.com/magicmetrics-call-recordings/calls/bc1b88a3-0784-4458-a252-54708005256d/4ae0b54be0c74a7195ac49f806bb33b7.wav",
      uploadedAt: "2026-06-06T12:47:19.361892+00:00",
      filename: "Call recording Pandu_260606.wav",
      mimeType: "audio/x-wav",
      size: 8763244,
    },
  },
  {
    id: "845c5c27-488b-4d67-a750-3c3fa5003d5a",
    title: "Securing a visit commitment",
    recording: {
      url: "https://storage.googleapis.com/magicmetrics-call-recordings/calls/845c5c27-488b-4d67-a750-3c3fa5003d5a/34a425f385f7418491de9f2d44889711.m4a",
      uploadedAt: "2026-06-14T15:36:18.054952+00:00",
      filename: "Call recording Ramana_260614_111839.m4a",
      mimeType: "audio/m4a",
      size: 1803457,
    },
  },
];
